import React, { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import Header from './Header';
import { Routes } from '../../routes';
import DateTabs from '../../components/DateTabs/DateTabs';
import { dateToUrlDate, getDateTabsTimestampsFrom, getDateTimestamp, throttle, } from '../../shared/functions';
import routesPaths from '../../routes/routesPaths';
import useAppGlobalData from '../../hooks/useAppGlobalData';
import { useNewspapersActionsDispatches } from '../../hooks/useNewspapers';
import useRouteMatchParams from '../../hooks/useRouteMatchParams';
import onMac from '../../shared/detect-user-agent';
import './app.style.scss';
/**
 * App component: renders the scaffold that contains the entire application
 * @constructor
 */
var App = function () {
    var _a = useAppGlobalData(), selectedCriteria = _a.selectedCriteria, selectedDate = _a.selectedDate;
    var fetchNewspapersByMonthAndPublication = useNewspapersActionsDispatches().fetchNewspapersByMonthAndPublication;
    var _b = useState(0), selectedDateTimestamp = _b[0], setSliderSelectedDateTimestamp = _b[1];
    var _c = useState([]), timestamps = _c[0], setTimestamps = _c[1];
    var fetchNewspapersMonthlyView = throttle(function (currentDate, id) {
        return fetchNewspapersByMonthAndPublication(currentDate, id);
    });
    var history = useHistory();
    var location = useLocation();
    var monthlyViewParams = useRouteMatchParams({
        path: routesPaths.newspaperMonthViewPage,
        exact: true,
    });
    var dailyViewParams = useRouteMatchParams(routesPaths.newspaperPage);
    var detailViewParams = useRouteMatchParams(routesPaths.newspaperDetailPage);
    var monthlyView = useMemo(function () { return !!selectedCriteria; }, [selectedCriteria]);
    var isApp = useMemo(function () {
        var query = new URLSearchParams(location.search);
        var appParam = query.get('sport24app');
        return appParam === 'true';
    }, [location]);
    // Takes care of ad refreshing
    var refreshPubAds = useCallback(function () {
        window.googletag = window.googletag || {};
        window.googletag.cmd = window.googletag.cmd || [];
        window.googletag.cmd.push(function () { return window.googletag.pubads().refresh(); });
    }, []);
    // Refresh ads both on app mount and every time the app navigates
    useEffect(function () { return refreshPubAds(); }, []);
    useMemo(function () { return history.listen(function () { return refreshPubAds(); }); }, [history, refreshPubAds]);
    // Sets the correct data when a new date is selected from the slider.
    // Also:
    // * if in daily view, updates the url
    // * if in monthly view, directly fetches the necessary data (in this case the url stays the same)
    // Does NOT update the redux state, since that is only dependent on changes from the calendar.
    var setDateFromSlider = useCallback(function (ts) {
        setSliderSelectedDateTimestamp(ts);
        if (monthlyView && (monthlyViewParams === null || monthlyViewParams === void 0 ? void 0 : monthlyViewParams.publicationId)) {
            fetchNewspapersMonthlyView(moment(ts), monthlyViewParams.publicationId);
        }
        else {
            history.push(!isApp
                ? routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(moment(ts))
                : routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(moment(ts)) + "?sport24app=true");
        }
    }, [
        history,
        routesPaths,
        setSliderSelectedDateTimestamp,
        fetchNewspapersMonthlyView,
        isApp,
        monthlyView,
        monthlyViewParams,
    ]);
    // Takes date and uses it to set data for dateslider
    var setDateSlider = useCallback(function (date) {
        var dateTS = getDateTimestamp(date);
        setSliderSelectedDateTimestamp(dateTS);
        setTimestamps(getDateTabsTimestampsFrom(dateTS, monthlyView ? 'months' : 'days'));
    }, [
        getDateTimestamp,
        setSliderSelectedDateTimestamp,
        setTimestamps,
        getDateTabsTimestampsFrom,
        monthlyView,
    ]);
    // If selected date changes (i.e. the calendar changed), set date slider to correct value
    useEffect(function () { return setDateSlider(selectedDate); }, [selectedDate, monthlyView]);
    // If date in url doesn't equal date in slider (e.g. when coming from detail view), adjust date in slider.
    useEffect(function () {
        if ((dailyViewParams === null || dailyViewParams === void 0 ? void 0 : dailyViewParams.date) &&
            dailyViewParams.date !== dateToUrlDate(moment(selectedDateTimestamp))) {
            setDateSlider(dailyViewParams.date);
        }
    }, [dailyViewParams, selectedDateTimestamp, monthlyView]);
    return (React.createElement("div", { className: "tfm-app " + (onMac ? 'tfm-app--on-mac' : '') },
        React.createElement(Header, { detailParams: detailViewParams, dailyParams: dailyViewParams, monthlyParams: monthlyViewParams }),
        React.createElement("main", { className: "tfm-app-content" },
            React.createElement("div", { className: "horizontal-slider-wrapper" },
                React.createElement(DateTabs, { className: "horizontal-slider-content", timestamps: timestamps, onChange: setDateFromSlider, value: selectedDateTimestamp, monthlyView: monthlyView })),
            React.createElement(Suspense, { fallback: React.createElement("div", null, "Loading...") },
                React.createElement(Routes, null)))));
};
export default memo(App);
